import React, { AnchorHTMLAttributes } from 'react';

import { Placement } from '@floating-ui/react';

import { Pagination } from '@travauxlib/shared/src/components/DesignSystem/components/Pagination';
import { usePagination } from '@travauxlib/shared/src/hooks/usePagination';

import { BaseTable } from './BaseTable';
import { useSorting } from './hooks/useSorting';
import { ColumnConfig, Item, RowAction, SizePaddingClassName, Sorting } from './types';

import { ActionListProps } from '../ActionList';
export type { ColumnConfig } from './types';

type Props<T> = {
  className?: string;
  title?: string;
  size?: SizePaddingClassName;
  columnConfigs: ColumnConfig<T>[];
  items: Item<T>[];
  onRowClick?: (item: T) => {
    action?: () => void;
    to?: string;
    href?: AnchorHTMLAttributes<T>['href'];
    target?: AnchorHTMLAttributes<T>['target'];
  };
  rowActions?: (item: T) => RowAction[] | React.ReactElement;
  actionListProps?: Partial<ActionListProps<T>>;
  // TODO prevent the use of fixFirstColumn if an item has additionalRowContent prop
  fixedColumn?: 'first' | 'last';
  renderAdditionalRowContent?: (item: T) => React.ReactElement;
  itemsPerPage?: number;
  onChangePage?: (page: number) => void;
  defaultPage?: number;
  defaultSort?: { columnKey: keyof T; order: 'asc' | 'desc' };
  onSort?: (sorting: Sorting<T>) => void;
  wrapLines?: boolean;
  inline?: boolean;
  autoResetPagination?: boolean;
  disableRow?: (item: T) => boolean;
  footer?: React.ReactElement;
  helperFooter?: React.ReactElement;
  paginationFooter?: React.ReactElement;
  rowActionsPosition?: Placement;
  emptyState?: React.ReactElement;
};

export const Table = <T extends object>(props: Props<T>): React.ReactElement => {
  const [sortedItems, onSortChanged, shouldDisplayChevron] = useSorting<Item<T>>(
    props.items,
    props.defaultSort,
    props.onSort,
  );
  const pagination = usePagination(
    sortedItems,
    props.itemsPerPage || Infinity,
    props.autoResetPagination,
    props.onChangePage,
    props.defaultPage,
  );

  return (
    <BaseTable
      {...props}
      items={pagination.items}
      onSortChanged={onSortChanged}
      shouldDisplayChevron={shouldDisplayChevron}
      paginationFooter={
        props.paginationFooter ||
        (props.itemsPerPage !== undefined ? (
          <div className="flex justify-end">
            <Pagination {...pagination} />
          </div>
        ) : undefined)
      }
    />
  );
};
